import { useMemo, useState } from "react";
import { t } from "ttag";

import {
  SettingsPageWrapper,
  SettingsSection,
} from "metabase/admin/components/SettingsSection";
import {
  useListAdminPersonalAccessTokensQuery,
  useRevokeAdminPersonalAccessTokenMutation,
} from "metabase/api";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { ClientSortableTable } from "metabase/common/components/Table";
import { useLocale } from "metabase/common/hooks/use-locale/use-locale";
import { formatDateTimeWithUnit } from "metabase/lib/formatting/date";
import { Button, Group, Modal, Stack, Text, Title } from "metabase/ui";
import type { AdminPersonalAccessToken } from "metabase-types/api";

const formatMaybeDate = (value: string | null) =>
  value ? formatDateTimeWithUnit(value, "minute") : t`Never`;

const formatStatus = (token: AdminPersonalAccessToken) => {
  switch (token.status) {
    case "active":
      return t`Active`;
    case "expired":
      return t`Expired`;
    case "revoked":
      return t`Revoked`;
  }
};

const formatOwner = (token: AdminPersonalAccessToken) => {
  const owner = token.owner;

  if (!owner) {
    return t`Unknown`;
  }

  const fullName = [owner.first_name, owner.last_name]
    .filter(Boolean)
    .join(" ");
  return fullName || owner.email;
};

function RevokePersonalAccessTokenModal({
  token,
  onClose,
}: {
  token: AdminPersonalAccessToken;
  onClose: () => void;
}) {
  const [revokeToken, { isLoading }] =
    useRevokeAdminPersonalAccessTokenMutation();

  return (
    <Modal opened onClose={onClose} title={t`Revoke personal access token`}>
      <Stack gap="lg">
        <Text>
          {t`This token will stop working immediately for the user who owns it.`}
        </Text>
        <Group justify="flex-end">
          <Button onClick={onClose}>{t`Cancel`}</Button>
          <Button
            color="error"
            variant="filled"
            loading={isLoading}
            onClick={async () => {
              await revokeToken(token.id).unwrap();
              onClose();
            }}
          >
            {t`Revoke token`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export const ManagePersonalAccessTokens = () => {
  const locale = useLocale();
  const [activeToken, setActiveToken] =
    useState<AdminPersonalAccessToken | null>(null);
  const columns = [
    { key: "name", name: t`Token name` },
    { key: "owner", name: t`Owner` },
    { key: "status", name: t`Status` },
    { key: "expires_at", name: t`Expires` },
    { key: "last_used_at", name: t`Last used` },
    { key: "actions", name: "", sortable: false },
  ];
  const { data, isLoading, error } = useListAdminPersonalAccessTokensQuery();

  const rows = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    return [...data].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  return (
    <SettingsPageWrapper title={t`Personal access tokens`}>
      <SettingsSection>
        {activeToken && (
          <RevokePersonalAccessTokenModal
            token={activeToken}
            onClose={() => setActiveToken(null)}
          />
        )}
        <Stack gap="lg">
          <Stack gap="xs">
            <Title order={2}>{t`Manage Personal Access Tokens`}</Title>
            <Text c="text-secondary">
              {t`View and revoke user-owned tokens without affecting the existing admin API key feature.`}
            </Text>
          </Stack>
          {isLoading || error ? (
            <DelayedLoadingAndErrorWrapper loading={isLoading} error={error} />
          ) : rows.length === 0 ? (
            <Text c="text-secondary">{t`No personal access tokens yet.`}</Text>
          ) : (
            <ClientSortableTable
              columns={columns}
              rows={rows}
              locale={locale}
              rowRenderer={(token: AdminPersonalAccessToken) => (
                <tr>
                  <td>{token.name}</td>
                  <td>{formatOwner(token)}</td>
                  <td>{formatStatus(token)}</td>
                  <td>{formatMaybeDate(token.expires_at)}</td>
                  <td>{formatMaybeDate(token.last_used_at)}</td>
                  <td>
                    {token.status === "active" && (
                      <Button
                        compact
                        variant="subtle"
                        onClick={() => setActiveToken(token)}
                      >
                        {t`Revoke`}
                      </Button>
                    )}
                  </td>
                </tr>
              )}
            />
          )}
        </Stack>
      </SettingsSection>
    </SettingsPageWrapper>
  );
};
